import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useParams } from 'react-router-dom'

import HttpStatus from 'components/Common/HttpStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ErrorPage from 'components/Pages/ErrorPage'
import {
  fetchLandingPage,
} from 'actions/ContentActions'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Content from 'components/Pages/LandingPage/Content'
import { mediaQueryUp } from 'components/utils/breakpoint'
import HowItWorksBanner from 'components/Pages/FrontPage/HowItWorksBanner'
import config from 'constants/config'
import Hero from './Hero'
import { getContentPageKey } from 'lib/url/contentUtils'
import TextButton from 'LuxKit/Button/TextButton'
import { getListName } from 'analytics/enhanced-ecommerce'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { FLASH_AND_TAO_OFFERS_TYPES } from 'constants/offerList'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const CenteredSpacing = styled.div`
  text-align: center;
`

const ResponsiveSpacing = styled.div`
  margin-top: ${rem(40)};
  margin-bottom: ${rem(40)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(65)};
    margin-bottom: ${rem(65)};
  }
`

interface LandingPageContainerProps {
  typeOfPage: string
}

interface Props extends LandingPageContainerProps {
  currency: string
  pageSlug: string
  landingPage: App.ContentHolidayPage | App.ContentDestinationPage
  regionCode: string
  error?: any
}

function LandingPage(props: Props) {
  const intl = useIntl()
  const { error, landingPage, typeOfPage, regionCode, pageSlug } = props
  const fetching = useAppSelector(state => state.content.fetching)

  const {
    title,
    subTitle,
    metaDescription,
    filters,
    image,
    content,
    hideSignUpOverlay,
    backgroundColour,
  } = landingPage ?? {}

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchLandingPage(pageSlug, typeOfPage))
  }, [dispatch, pageSlug, typeOfPage])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Cruise Page Offer List'),
  )

  if (error) {
    if (error.status === 404) {
      return <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>
    } else {
      return <ErrorPage error={error} />
    }
  }
  if (fetching) {
    return <LoadingIndicator />
  } else if (!landingPage && !fetching) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  const offerListFilters: App.OfferListFilters = {
    ...filters,
    offerTypes: FLASH_AND_TAO_OFFERS_TYPES,
    frontPageVisibleOnly: true,
  }

  const metaData = {
    title: config.title,
    description: metaDescription || intl.formatMessage({ id: 'lZPArUyYP70NScQ-9Sm7', defaultMessage: 'Make your dream {destination} holiday a reality! Discover the best that {destination} has on offer and save with Luxury Escapes holiday packages & accommodation deals.', description: 'Default meta description for landing page when none is provided from CMS' }, { destination: title }),
  }

  const headTitle = `${title} - ${config.title} ${regionCode}`

  const offerListExtas: Array<OfferListV2Item> = [{
    id: 'message',
    position: 'start',
    element: (
      <ResponsiveSpacing>
        <LayoutContainer size="sm">
          <VerticalSpacer gap={20}>
            <VerticalSpacer gap={12}>
              <Heading variant="heading3" align="center">
                {intl.formatMessage({ id: 'ZtFwobN-_LoPY5xfjFhd', defaultMessage: 'We are currently sold out of our {destination} escapes!', description: 'Heading shown when no offers are available for a destination on landing page' }, { destination: title })}
              </Heading>
              <BodyText variant="large" align="center">
                {intl.formatMessage({ id: 'QfibTt7Mw1coPTLF0MM_', defaultMessage: 'Our travel experts are working hard on curating new {destination} offers. In the meantime check out our available offers in other exciting destinations.', description: 'Message shown when no offers are available for a destination on landing page' }, { destination: title })}
              </BodyText>
            </VerticalSpacer>
            <CenteredSpacing>
              <TextButton kind="primary" to="/">
                {intl.formatMessage({ id: 'RIFEI9wYg9YAv3uoMvV0', defaultMessage: 'Browse all available offers', description: 'Button to browse all available offers when destination has no current deals' })}
              </TextButton>
            </CenteredSpacing>
          </VerticalSpacer>
        </LayoutContainer>
      </ResponsiveSpacing>
    ),
    show: 'hasNoOffers',
  }, {
    id: 'howitworks',
    position: 3,
    element: <HowItWorksBanner />,
  }]

  return (
    <Pane background={backgroundColour === 'white' ? undefined : 'none'}>
      <VerticalSpacer gap={20}>
        <Hero
          title={intl.formatMessage({ id: 'tZS78--T44rv2sSJQle3', defaultMessage: '{destination} Holiday Packages', description: 'Hero title for destination landing page showing holiday packages' }, { destination: title })}
          subTitle={subTitle}
          image={image}
          headTitle={headTitle}
          typeOfPage={typeOfPage}
          metaData={metaData}
          version="landing"
          hideSignUpOverlay={hideSignUpOverlay}
          back={{
            link: '/',
            title: intl.formatMessage({ id: 'ZULiYgMae6nNXDubUSvz', defaultMessage: 'View all offers', description: 'Back navigation link text to return to all offers from landing page' }),
          }}
        />

        <LayoutContainer>
          <OfferListEventsProvider tracking={tracking}>
            <OfferList
              filters={offerListFilters}
              interstitials={offerListExtas}
            />
          </OfferListEventsProvider>
        </LayoutContainer>

        {(content.video || content.description) &&
          <Content
            title={title}
            header={content.title}
            videoUrl={content.video}
            description={content.description}
          />}
      </VerticalSpacer>
    </Pane>
  )
}

type MappedProps = Pick<Props, 'currency' | 'landingPage' | 'error' | 'regionCode'>

function mapStateToProps(state: App.State, ownProps: Omit<Props, keyof MappedProps>): MappedProps {
  const key = getContentPageKey(ownProps.pageSlug, ownProps.typeOfPage)

  return {
    currency: state.geo.currentCurrency,
    landingPage: state.content.landingPages[key] as any,
    error: state.content.landingPageErrors[key],
    regionCode: state.geo.currentRegionCode,
  }
}

const ConnectedLandingPage = connect(mapStateToProps)(LandingPage)

function LandingPageContainer(props: LandingPageContainerProps) {
  const { slug = '' } = useParams<'slug'>()

  return <ConnectedLandingPage {...props} pageSlug={slug} />
}

export default LandingPageContainer
